/**
 * Film Comps Formatting Utilities
 * Provides formatting functions for displaying comp data in the UI
 */

/**
 * Format confidence score as percentage
 * @deprecated Confidence scores have been removed from the Film Comps feature.
 * This function is kept for backward compatibility but should not be used.
 * @param {number} score - Confidence score (0-1)
 * @param {number} decimals - Number of decimal places (default: 0)
 * @returns {string} - Formatted percentage string
 */
export const formatConfidenceScore = (score, decimals = 0) => {
  if (typeof score !== 'number' || isNaN(score)) return '0%';
  const percentage = Math.round(score * 100 * Math.pow(10, decimals)) / Math.pow(10, decimals);
  return `${percentage}%`;
};

/**
 * Format date for display
 * @param {string|Date} dateString - ISO date string or Date object
 * @param {Object} options - Formatting options
 * @param {boolean} options.includeTime - Whether to include time (default: false)
 * @param {boolean} options.relative - Whether to show relative time (default: false)
 * @returns {string} - Formatted date string
 */
export const formatDate = (dateString, options = {}) => {
  const { includeTime = false, relative = false } = options;
  
  if (!dateString) return 'N/A';
  
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return 'Invalid Date';
  
  if (relative) {
    return formatRelativeTime(date);
  }
  
  const options_obj = {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  };
  
  if (includeTime) {
    options_obj.hour = '2-digit';
    options_obj.minute = '2-digit';
  }
  
  return date.toLocaleDateString('en-US', options_obj);
};

/**
 * Format relative time (e.g., "2 days ago", "3 hours ago")
 * @param {Date} date - Date to format
 * @returns {string} - Relative time string
 */
export const formatRelativeTime = (date) => {
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);
  
  if (diffInSeconds < 60) {
    return 'Just now';
  }
  
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''} ago`;
  }
  
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`;
  }
  
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) {
    return `${diffInDays} day${diffInDays !== 1 ? 's' : ''} ago`;
  }
  
  const diffInWeeks = Math.floor(diffInDays / 7);
  if (diffInWeeks < 4) {
    return `${diffInWeeks} week${diffInWeeks !== 1 ? 's' : ''} ago`;
  }
  
  const diffInMonths = Math.floor(diffInDays / 30);
  if (diffInMonths < 12) {
    return `${diffInMonths} month${diffInMonths !== 1 ? 's' : ''} ago`;
  }
  
  const diffInYears = Math.floor(diffInDays / 365);
  return `${diffInYears} year${diffInYears !== 1 ? 's' : ''} ago`;
};

/**
 * Format runtime in seconds to human readable format
 * @param {number} runtimeInSeconds - Runtime in seconds
 * @returns {string} - Formatted runtime string (e.g., "2h 15m")
 */
export const formatRuntime = (runtimeInSeconds) => {
  if (typeof runtimeInSeconds !== 'number' || isNaN(runtimeInSeconds)) return 'N/A';
  
  const hours = Math.floor(runtimeInSeconds / 3600);
  const minutes = Math.floor((runtimeInSeconds % 3600) / 60);
  
  if (hours > 0) {
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  }
  
  return `${minutes}m`;
};

/**
 * Format matching criteria for display
 * @param {Object} criteria - Matching criteria object
 * @returns {Array} - Array of formatted criteria objects
 */
export const formatMatchingCriteria = (criteria) => {
  if (!criteria || typeof criteria !== 'object') return [];
  
  const criteriaLabels = {
    genre: 'Genre',
    creative: 'Creative',
    studio: 'Studio',
    seasonal: 'Seasonal',
    budget: 'Budget'
  };
  
  return Object.entries(criteria).map(([key, value]) => ({
    label: criteriaLabels[key] || key.charAt(0).toUpperCase() + key.slice(1),
    value: typeof value === 'number' ? value : 0,
    percentage: formatConfidenceScore(value, 1)
  }));
};

/**
 * Get color for confidence score
 * @deprecated Confidence scores have been removed from the Film Comps feature.
 * This function is kept for backward compatibility but should not be used.
 * @param {number} score - Confidence score (0-1)
 * @returns {string} - CSS color class or hex color
 */
export const getConfidenceColor = (score) => {
  if (typeof score !== 'number' || isNaN(score)) return '#6B7280'; // gray
  
  if (score >= 0.7) return '#10B981'; // green
  if (score >= 0.4) return '#F59E0B'; // yellow
  return '#EF4444'; // red
};

/**
 * Get confidence level text
 * @deprecated Confidence scores have been removed from the Film Comps feature.
 * This function is kept for backward compatibility but should not be used.
 * @param {number} score - Confidence score (0-1)
 * @returns {string} - Confidence level text
 */
export const getConfidenceLevel = (score) => {
  if (typeof score !== 'number' || isNaN(score)) return 'Unknown';
  
  if (score >= 0.8) return 'High';
  if (score >= 0.6) return 'Medium';
  if (score >= 0.4) return 'Low';
  return 'Very Low';
};

/**
 * Format comparison metrics for display
 * @param {Object} metrics - Comparison metrics object
 * @returns {Array} - Array of formatted metric objects
 */
export const formatComparisonMetrics = (metrics) => {
  if (!metrics || typeof metrics !== 'object') return [];
  
  const metricLabels = {
    daysDifference: 'Days Difference',
    weeksDifference: 'Weeks Difference',
    runtimeDifference: 'Runtime Difference',
    releaseYearDifference: 'Release Year Difference'
  };
  
  return Object.entries(metrics).map(([key, value]) => ({
    label: metricLabels[key] || key.charAt(0).toUpperCase() + key.slice(1),
    value: typeof value === 'number' ? value : 0,
    formatted: formatMetricValue(key, value)
  }));
};

/**
 * Format individual metric value
 * @param {string} key - Metric key
 * @param {number} value - Metric value
 * @returns {string} - Formatted value string
 */
export const formatMetricValue = (key, value) => {
  if (typeof value !== 'number' || isNaN(value)) return 'N/A';
  
  switch (key) {
    case 'daysDifference':
      return `${Math.abs(value)} day${Math.abs(value) !== 1 ? 's' : ''} ${value >= 0 ? 'later' : 'earlier'}`;
    case 'weeksDifference':
      return `${Math.abs(value)} week${Math.abs(value) !== 1 ? 's' : ''} ${value >= 0 ? 'later' : 'earlier'}`;
    case 'runtimeDifference':
      return `${Math.abs(value)}s ${value >= 0 ? 'longer' : 'shorter'}`;
    case 'releaseYearDifference':
      return `${Math.abs(value)} year${Math.abs(value) !== 1 ? 's' : ''} ${value >= 0 ? 'later' : 'earlier'}`;
    default:
      return value.toString();
  }
};

/**
 * Format comp type for display
 * @param {string} type - Comp type ('modeled' or 'greenlight')
 * @returns {Object} - Formatted comp type object
 */
export const formatCompType = (type) => {
  const typeConfig = {
    modeled: {
      label: 'Modeled',
      color: '#3B82F6', // blue
      icon: '🤖',
      description: 'AI-generated comparison'
    },
    greenlight: {
      label: 'Greenlight',
      color: '#10B981', // green
      icon: '✅',
      description: 'Expert-curated comparison'
    }
  };
  
  return typeConfig[type] || {
    label: 'Unknown',
    color: '#6B7280',
    icon: '❓',
    description: 'Unknown comparison type'
  };
};

/**
 * Format assignment reason for display
 * @param {string} reason - Assignment reason
 * @param {number} maxLength - Maximum length before truncation (default: 100)
 * @returns {string} - Formatted reason string
 */
export const formatAssignmentReason = (reason, maxLength = 100) => {
  if (!reason || typeof reason !== 'string') return 'No reason provided';
  
  const trimmed = reason.trim();
  if (trimmed.length <= maxLength) return trimmed;
  
  return trimmed.substring(0, maxLength - 3) + '...';
};

/**
 * Format film title for display
 * @param {string} title - Film title
 * @param {number} maxLength - Maximum length before truncation (default: 50)
 * @returns {string} - Formatted title string
 */
export const formatFilmTitle = (title, maxLength = 50) => {
  if (!title || typeof title !== 'string') return 'Unknown Title';
  
  const trimmed = title.trim();
  if (trimmed.length <= maxLength) return trimmed;
  
  return trimmed.substring(0, maxLength - 3) + '...';
};

/**
 * Format IMDB ID for display
 * @param {string} imdbId - IMDB ID
 * @returns {string} - Formatted IMDB ID
 */
export const formatIMDBId = (imdbId) => {
  if (!imdbId || typeof imdbId !== 'string') return 'N/A';
  return imdbId.toUpperCase();
};

/**
 * Format production status for display
 * @param {string} status - Production status
 * @returns {Object} - Formatted status object
 */
export const formatProductionStatus = (status) => {
  if (!status || typeof status !== 'string') {
    return {
      label: 'Unknown',
      color: '#6B7280',
      icon: '❓'
    };
  }
  
  const statusConfig = {
    'Released': {
      label: 'Released',
      color: '#10B981',
      icon: '🎬'
    },
    'In Production': {
      label: 'In Production',
      color: '#F59E0B',
      icon: '🎭'
    },
    'Post Production': {
      label: 'Post Production',
      color: '#8B5CF6',
      icon: '✂️'
    },
    'Pre Production': {
      label: 'Pre Production',
      color: '#3B82F6',
      icon: '📝'
    },
    'Announced': {
      label: 'Announced',
      color: '#6B7280',
      icon: '📢'
    }
  };
  
  return statusConfig[status] || {
    label: status,
    color: '#6B7280',
    icon: '❓'
  };
};

/**
 * Format categories/genres for display
 * @param {Array} categories - Array of category strings
 * @param {number} maxDisplay - Maximum number of categories to display (default: 3)
 * @returns {Object} - Formatted categories object
 */
export const formatCategories = (categories, maxDisplay = 3) => {
  if (!Array.isArray(categories) || categories.length === 0) {
    return {
      display: ['Unknown'],
      remaining: 0,
      full: []
    };
  }
  
  const validCategories = categories.filter(cat => typeof cat === 'string' && cat.trim().length > 0);
  
  if (validCategories.length <= maxDisplay) {
    return {
      display: validCategories,
      remaining: 0,
      full: validCategories
    };
  }
  
  return {
    display: validCategories.slice(0, maxDisplay),
    remaining: validCategories.length - maxDisplay,
    full: validCategories
  };
};

/**
 * Format analytics data for display
 * @param {Object} analytics - Analytics data object
 * @returns {Object} - Formatted analytics object
 */
export const formatAnalytics = (analytics) => {
  if (!analytics || typeof analytics !== 'object') {
    return {
      totalComps: 0,
      modeledComps: 0,
      greenlightComps: 0,
      activeComps: 0,
      inactiveComps: 0,
      averageConfidenceScore: 0,
      averageConfidencePercentage: '0%'
    };
  }
  
  return {
    totalComps: analytics.totalComps || 0,
    modeledComps: analytics.modeledComps || 0,
    greenlightComps: analytics.greenlightComps || 0,
    activeComps: analytics.activeComps || 0,
    inactiveComps: analytics.inactiveComps || 0,
    averageConfidenceScore: analytics.averageConfidenceScore || 0,
    averageConfidencePercentage: formatConfidenceScore(analytics.averageConfidenceScore || 0, 1)
  };
};

/**
 * Format error message for display
 * @param {Error|string} error - Error object or message
 * @returns {string} - Formatted error message
 */
export const formatErrorMessage = (error) => {
  if (typeof error === 'string') return error;
  if (error && error.message) return error.message;
  if (error && error.userMessage) return error.userMessage;
  return 'An unexpected error occurred';
};

/**
 * Format loading state message
 * @param {string} operation - Operation being performed
 * @returns {string} - Formatted loading message
 */
export const formatLoadingMessage = (operation = 'Loading') => {
  const messages = {
    'Loading': 'Loading...',
    'Fetching comps': 'Fetching film comps...',
    'Fetching comparison': 'Loading comparison data...',
    'Fetching candidates': 'Finding comp candidates...',
    'Assigning comp': 'Assigning comp...',
    'Updating comp': 'Updating comp...',
    'Deleting comp': 'Deleting comp...',
    'Bulk assigning': 'Processing bulk assignments...',
    'Fetching analytics': 'Loading analytics...',
    'Fetching history': 'Loading comp history...'
  };
  
  return messages[operation] || `${operation}...`;
};

// Export all formatting functions
const compFormatters = {
  formatConfidenceScore,
  formatDate,
  formatRelativeTime,
  formatRuntime,
  formatMatchingCriteria,
  getConfidenceColor,
  getConfidenceLevel,
  formatComparisonMetrics,
  formatMetricValue,
  formatCompType,
  formatAssignmentReason,
  formatFilmTitle,
  formatIMDBId,
  formatProductionStatus,
  formatCategories,
  formatAnalytics,
  formatErrorMessage,
  formatLoadingMessage
};

export default compFormatters;
